import { Comment, CommentAnchorType } from '@marketing-milk/interfaces'

/**
 * Client-side unresolved-comment counts for anchor indicators (v1.4a).
 *
 * "Unresolved" counts only **root** comments (`parentCommentID == null`) that are
 * not resolved — the thread UI resolves per-root, and replies don't carry their
 * own resolve state, so a reply should never inflate an anchor's badge. Counts are
 * derived from already-hydrated comments (e.g. `SprintPacketHydrated.comments` or a
 * surface's own `useComments` result); there is no count endpoint.
 */

export const unresolvedCountKey = (anchorType: CommentAnchorType, anchorID: string): string =>
  `${anchorType}:${anchorID}`

const isUnresolvedRoot = (c: Comment): boolean =>
  (c.parentCommentID === null || c.parentCommentID === undefined) && !c.resolved

/** Unresolved root-comment count for a single anchor. */
export const countUnresolvedRoots = (
  comments: Comment[],
  anchorType: CommentAnchorType,
  anchorID: string
): number =>
  comments.reduce(
    (n, c) =>
      c.anchorType === anchorType && c.anchorID === anchorID && isUnresolvedRoot(c) ? n + 1 : n,
    0
  )

/**
 * Total unresolved root comments across a flat list, for a single packet-wide badge
 * (the "All comments" entry point). Equivalent to summing
 * {@link buildUnresolvedCountMap}'s values, but skips the per-anchor map.
 */
export const countUnresolvedTotal = (comments: Comment[]): number =>
  comments.reduce((n, c) => (isUnresolvedRoot(c) ? n + 1 : n), 0)

/**
 * One-pass map of unresolved root-comment counts keyed by `<type>:<id>`, for hosts
 * (the packet) that render many anchors from a single flat comment list — O(n) once
 * instead of filtering the list per indicator.
 */
export const buildUnresolvedCountMap = (comments: Comment[]): Map<string, number> => {
  const map = new Map<string, number>()
  for (const c of comments) {
    if (!isUnresolvedRoot(c)) continue
    const key = unresolvedCountKey(c.anchorType, c.anchorID)
    map.set(key, (map.get(key) ?? 0) + 1)
  }
  return map
}
